/* StateSummary — four-layer state summary (from stateReauditMatrix) shown right after state selection. */
import React from 'react';
import { Building2, MapPin, Eye, Factory, ChevronRight, Wrench, Car } from 'lucide-react';
import { CoverImg } from './shared';

const PILLS = [
  { key: 'localPlatforms', label: 'Local platforms', icon: Building2, tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  { key: 'cityCompanyCards', label: 'City-company cards', icon: MapPin, tone: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  { key: 'rolePreviews', label: 'Role previews', icon: Eye, tone: 'bg-indigo-50 text-indigo-800 border-indigo-200' },
  { key: 'manufacturingEmployers', label: 'Official manufacturing employers', icon: Factory, tone: 'bg-slate-100 text-slate-800 border-slate-200' },
];

export function StateCountPills({ counts }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid="bc-state-pills">
      {PILLS.map(({ key, label, icon: Icon, tone }) => (
        <span key={key} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold ${tone}`} data-testid={`bc-pill-${key}`}>
          <Icon size={12} /> <span className="tabular-nums font-black">{counts[key]}</span> {label}
        </span>
      ))}
    </div>
  );
}

function Layer({ icon: Icon, title, count, children, testId, cover }) {
  return (
    <div className="bg-white rounded-3xl border-2 border-gray-100 p-5" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          {cover?.url && (
            <CoverImg src={cover.url} alt={cover.alt} className="w-24 sm:w-32 shrink-0 rounded-xl overflow-hidden" testId={`${testId}-cover`} />
          )}
          <h3 className="font-extrabold text-gray-900 text-sm flex items-center gap-2 min-w-0">
            <span className="w-7 h-7 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 text-white flex items-center justify-center shrink-0"><Icon size={13} /></span>
            {title}
          </h3>
        </div>
        <span className="text-xs font-black text-gray-500 tabular-nums">{count}</span>
      </div>
      {children}
    </div>
  );
}

/* Hero cover for the currently selected state only (eager). */
export function StateCover({ state }) {
  if (!state?.coverImageUrl) return null;
  return (
    <div className="relative mt-8 max-w-3xl mx-auto rounded-3xl overflow-hidden shadow-md" data-testid={`bc-state-cover-${state.stateCode.toLowerCase()}`}>
      <CoverImg src={state.coverImageUrl} alt={state.coverImageAlt} eager />
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent px-5 pb-4 pt-12">
        <p className="text-[10px] font-extrabold uppercase tracking-widest text-white/80">Blue-Collar Jobs · Official routes</p>
        <p className="text-white text-xl md:text-2xl font-extrabold leading-tight">{state.stateName}</p>
      </div>
    </div>
  );
}

export function StateSummary({ state, rule, covers = {}, onPickCity }) {
  const c = state.counts;
  return (
    <section className="mt-8" data-testid="bc-state-summary">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div>
          <p className="text-[11px] font-extrabold uppercase tracking-widest text-amber-700">Step 2 — {state.stateName} at a glance</p>
          <p className="text-xs text-gray-500 mt-1">Official-company audit on {state.auditedOn}. Pick a city below to open its verified company cards. Images are illustrative work scenes only.</p>
        </div>
        <StateCountPills counts={c} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Layer icon={Building2} title="Local platform companies" count={c.localPlatforms} testId="bc-layer-platforms" cover={covers.localPlatformCompanies}>
          <div className="flex flex-wrap gap-2">
            {state.localPlatforms.map((p) => (
              <span key={p.companyId} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-gray-200 bg-gray-50 text-gray-800 px-2.5 py-1 rounded-full" data-testid={`bc-platform-${p.companyId}`}>
                {p.section === 'vehicle' ? <Car size={11} className="text-sky-600" /> : <Wrench size={11} className="text-amber-600" />} {p.companyName}
              </span>
            ))}
            {state.localPlatforms.length === 0 && <p className="text-xs text-gray-500">No verified local platform card in this state yet.</p>}
          </div>
          <p className="text-[11px] text-gray-500 mt-3">Company-mediated work only. A platform listed here still needs city confirmation in the official flow.</p>
        </Layer>

        <Layer icon={MapPin} title="Verified city–company cards" count={c.cityCompanyCards} testId="bc-layer-cities" cover={covers.cityCompanyCards}>
          <div className="flex flex-wrap gap-2">
            {state.cityCards.map((ct) => (
              <button key={ct.cityName} type="button" onClick={() => onPickCity(ct.cityName)}
                className="inline-flex items-center gap-1.5 text-xs font-bold border-2 border-emerald-200 bg-emerald-50 text-emerald-800 hover:border-emerald-400 px-3 py-1.5 rounded-full transition-colors min-h-[36px]"
                data-testid={`bc-summary-city-${ct.cityName.toLowerCase().replace(/\s+/g, '-')}`}>
                {ct.cityName} <span className="tabular-nums opacity-70">· {ct.count}</span> <ChevronRight size={12} />
              </button>
            ))}
          </div>
          <p className="text-[11px] text-gray-500 mt-3">Customer service is verified in these cities. Worker capacity, role, documents, costs and terms must be confirmed in the official company flow.</p>
        </Layer>

        <Layer icon={Eye} title="Role previews" count={c.rolePreviews} testId="bc-layer-roles" cover={covers.rolePreviews}>
          <p className="text-xs text-gray-800 leading-relaxed">
            Work explanations linked to official routes — open them inside a city card. They are <strong>not</strong> job postings, local vacancies, salary promises or guaranteed opportunities.
          </p>
        </Layer>

        <Layer icon={Factory} title="Official manufacturing employers" count={c.manufacturingEmployers} testId="bc-layer-manufacturing" cover={covers.officialManufacturingEmployers}>
          {state.manufacturingEmployers.length > 0 ? (
            <ul className="space-y-1.5">
              {state.manufacturingEmployers.slice(0, 6).map((e) => (
                <li key={e.employerId} className="text-xs text-gray-800 flex items-start gap-1.5">
                  <Factory size={11} className="mt-0.5 text-slate-500 shrink-0" />
                  <span><strong>{e.employerName}</strong> <span className="text-gray-500">· {e.localities.join(', ')}</span></span>
                </li>
              ))}
              {state.manufacturingEmployers.length > 6 && <li className="text-[11px] text-gray-500">+{state.manufacturingEmployers.length - 6} more — shown in full after you pick a city.</li>}
            </ul>
          ) : (
            <p className="text-xs text-gray-700 leading-relaxed">{rule}</p>
          )}
          {c.routeOnlyEmployers > 0 && (
            <p className="text-[11px] text-gray-500 mt-3">{c.routeOnlyEmployers} more employer route(s) without strict facility proof appear under “Check official route”.</p>
          )}
        </Layer>
      </div>
    </section>
  );
}
